"use client";

// ============================================================================
// SiFa Flow: Cookie-Consent-Banner
// ----------------------------------------------------------------------------
// Aktuell setzt SiFa Flow nur technisch notwendige Cookies (Auth-Session
// via Supabase) sowie Sentry-Fehlerdiagnose (DSGVO Art. 6 Abs. 1 lit. f
// berechtigtes Interesse — siehe sentry.client.config.ts: sendDefaultPii=false,
// EU-Region, Scrubber). Beides braucht keine Einwilligung.
//
// Dieser Banner ist die DSGVO-konforme Vorausstattung fuer den Fall, dass
// spaeter Analytics-/Marketing-Pixel eingebaut werden — Schema und Storage
// stehen, das UI muss dann nur den jeweiligen Toggle aktivieren.
//
// Storage-Key:   sifaflow_cookie_consent_v1
// Format:        { necessary:true, analytics:false, marketing:false, decided_at:ISO }
//
// Re-Open-Mechanismus: window.dispatchEvent(new Event("sifaflow:open-cookie-settings"))
// Ein Footer-Link "Cookie-Einstellungen" kann das Event triggern.
// ============================================================================

import { useEffect, useState, useCallback } from "react";
import Link from "next/link";
import { Cookie, Settings2, Check } from "lucide-react";

const STORAGE_KEY = "sifaflow_cookie_consent_v1";

interface ConsentState {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decided_at: string;
  version: number;
}

const CURRENT_VERSION = 1;

function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CURRENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(state: Omit<ConsentState, "decided_at" | "version">) {
  if (typeof window === "undefined") return;
  const full: ConsentState = {
    ...state,
    decided_at: new Date().toISOString(),
    version: CURRENT_VERSION,
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(full));
}

export function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      // Banner verzoegert einblenden, damit die Page LCP nicht leidet
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    } else {
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
    }
  }, []);

  // Re-open-Hook fuer Footer-Link "Cookie-Einstellungen"
  useEffect(() => {
    const handler = () => {
      const existing = readConsent();
      if (existing) {
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
      }
      setShowSettings(true);
      setOpen(true);
    };
    window.addEventListener("sifaflow:open-cookie-settings", handler);
    return () =>
      window.removeEventListener("sifaflow:open-cookie-settings", handler);
  }, []);

  const acceptAll = useCallback(() => {
    writeConsent({ necessary: true, analytics: true, marketing: true });
    setAnalytics(true);
    setMarketing(true);
    setOpen(false);
    setShowSettings(false);
  }, []);

  const acceptNecessary = useCallback(() => {
    writeConsent({ necessary: true, analytics: false, marketing: false });
    setAnalytics(false);
    setMarketing(false);
    setOpen(false);
    setShowSettings(false);
  }, []);

  const saveSelection = useCallback(() => {
    writeConsent({ necessary: true, analytics, marketing });
    setOpen(false);
    setShowSettings(false);
  }, [analytics, marketing]);

  if (!open) return null;

  // Banner ist im Root-Layout gemountet (außerhalb der Dashboard-Shell, die
  // .theme-v3 wrappt). Damit der Banner überall — Dashboard, Auth, Legal,
  // Landing — im neuen V3-Design (Safety-Orange #FF6B00, IBM Plex, schärfere
  // Radien) erscheint, trägt er seinen .theme-v3-Scope selbst. Alle inneren
  // var(--surface-*, --color-accent-500, --line-accent, --fg-*) ziehen damit
  // automatisch die V3-Werte. Siehe project_v3_dashboard_rollout.
  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      className="theme-v3 fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4 md:p-5 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pointer-events-none"
    >
      <div className="mx-auto max-w-3xl pointer-events-auto rounded-[var(--radius)] border border-[var(--line-accent)] bg-[var(--surface-2)] backdrop-blur-md shadow-2xl shadow-black/40">
        <div className="p-5 sm:p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--overlay-1)] hairline">
              <Cookie className="h-5 w-5 text-[var(--color-accent-500)]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                id="cookie-banner-title"
                className="heading-industrial text-base font-semibold text-[var(--fg-primary)]"
              >
                {showSettings ? "Cookie-Einstellungen" : "Cookies & Datenschutz"}
              </h2>
              <p className="mt-1 text-sm text-[var(--fg-secondary)] leading-relaxed">
                Wir verwenden ausschließlich technisch notwendige Cookies (Anmeldesitzung)
                sowie Sentry zur Fehlerdiagnose auf Basis berechtigten Interesses
                (Art. 6 Abs. 1 lit. f DSGVO, EU-Hosting, PII-Scrubbing). Optionale
                Analyse- oder Marketing-Cookies sind derzeit nicht aktiv.{" "}
                <Link
                  href="/datenschutz"
                  className="font-medium text-[var(--fg-primary)] underline decoration-[var(--color-accent-500)] underline-offset-4 hover:decoration-2"
                >
                  Mehr in der Datenschutzerklärung
                </Link>
                .
              </p>
            </div>
          </div>

          {showSettings && (
            <div className="space-y-2 pl-12">
              <CategoryRow
                title="Notwendig"
                description="Für Login & Tenant-Sitzung erforderlich. Können nicht deaktiviert werden."
                checked
                disabled
              />
              <CategoryRow
                title="Analyse (derzeit nicht aktiv)"
                description="Anonymisierte Nutzungsstatistiken. Wird aktuell nicht erhoben."
                checked={analytics}
                onChange={setAnalytics}
              />
              <CategoryRow
                title="Marketing (derzeit nicht aktiv)"
                description="Re-Targeting-Pixel. Wird aktuell nicht eingesetzt."
                checked={marketing}
                onChange={setMarketing}
              />
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 hairline-t">
            <button
              type="button"
              onClick={() => setShowSettings((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] transition-colors"
            >
              <Settings2 className="h-3.5 w-3.5" />
              {showSettings ? "Hinweis-Ansicht" : "Einstellungen anpassen"}
            </button>

            <div className="flex flex-col-reverse sm:flex-row gap-2">
              <button
                type="button"
                onClick={acceptNecessary}
                className="inline-flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium text-[var(--fg-secondary)] hover:bg-[var(--overlay-1)] transition-colors"
              >
                Nur notwendige
              </button>
              {showSettings ? (
                <button
                  type="button"
                  onClick={saveSelection}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-[var(--color-accent-500)] px-4 text-sm font-semibold text-[#1A1A1A] hover:opacity-90 transition-opacity"
                >
                  <Check className="h-4 w-4" />
                  Auswahl speichern
                </button>
              ) : (
                <button
                  type="button"
                  onClick={acceptAll}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-[var(--color-accent-500)] px-4 text-sm font-semibold text-[#1A1A1A] hover:opacity-90 transition-opacity"
                >
                  <Check className="h-4 w-4" />
                  Alle akzeptieren
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
}) {
  return (
    <label
      className={`flex items-start gap-3 rounded-md p-3 hairline ${
        disabled ? "opacity-70" : "cursor-pointer hover:bg-[var(--overlay-1)]"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-[var(--line-accent)] accent-[var(--color-accent-500)]"
      />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-[var(--fg-primary)]">
          {title}
        </span>
        <span className="block text-xs text-[var(--fg-muted)] mt-0.5 leading-relaxed">
          {description}
        </span>
      </span>
    </label>
  );
}
